import {Injectable, Injector} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, tap, throwError} from 'rxjs';
import {catchError} from "rxjs/operators";
import {SnackbarService} from '../_services/snackbar.service';
import {AuthService} from "../_services/auth.service";
import {MatDialog} from "@angular/material/dialog";
import {TimeoutNotifierService} from "../_services/timeout-notifier.service";
import {HANDLE_UPLOAD_ERRORS} from "../_utils/upload-errors.token";
import {environment} from '../../environments/environment';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    constructor(private snack: SnackbarService,
                private authService: AuthService,
                private dialog: MatDialog,
                private injector: Injector) {
    }

    private get timeoutNotifierService(): TimeoutNotifierService {
        return this.injector.get(TimeoutNotifierService);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(tap((httpEvent: HttpEvent<any>): void => {
            // Skip request
            if (httpEvent.type === 0) {
                return;
            }
            if (httpEvent instanceof HttpResponse) {
                if (httpEvent.headers.has('x-explain-timeout-seconds')) {
                    const rawTimeout: string | number | undefined = httpEvent.headers.get('x-explain-timeout-seconds')?.trim();
                    if (!rawTimeout || rawTimeout === 'None') {
                        return;
                    }
                    const timeoutSeconds: number = Number.parseInt(rawTimeout, 10);

                    if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) {
                        return;
                    }
                    // convert seconds to milliseconds
                    // Send a notification to the user before the timeout so they can refresh
                    this.timeoutNotifierService.timeoutNotifier(timeoutSeconds * 1000);
                }
            }
        })).pipe(catchError(error => {

            if (request.context.get(HANDLE_UPLOAD_ERRORS) && ![401, 403, 408].includes(error.status)) {
                return throwError(() => error);
            }

            if (!navigator.onLine) {
                // User is offline
                return throwError('You appear to be offline. Please check your internet connection and try again.');
            }

            const apiRequest = request.url.startsWith(`${environment.apiUrl}/`);
            const sessionTimedOut = error.status === 408 && error.error?.error === 'Timeout expired';
            if (apiRequest && (error.status === 403 || error.status === 401 || sessionTimedOut) && !location.pathname.includes('login')) {
                if (error.status === 403) {
                    this.snack.openErrorSnackBar('We\'re sorry, but you don\'t have permission to access this resource. If you believe this is an error, please contact support.');
                } else if (sessionTimedOut) {
                    this.dialog.closeAll();
                    this.snack.openErrorSnackBar('User logged out due to inactivity');
                    // Only return to previous location if the user had access to that page. (stops loop)
                    if (window.location.href.includes('returnUrl')) {
                        let url: URL = new URL(window.location.href);
                        url.searchParams.delete('returnUrl'); // Removes the 'returnUrl' parameter
                        this.authService.logout(encodeURIComponent(url.pathname + url.search));
                    } else {
                        this.authService.logout(encodeURIComponent(window.location.pathname + window.location.search));
                    }
                } else {
                    this.authService.logout()
                }

            }
            if (error.status === 413) {
                this.snack.openErrorSnackBar('The uploaded file/content is too large. Please upload a smaller one.')
                return throwError((): null => null);
            }
            if (error.status === 0) {
                if (request.body instanceof FormData) {
                    const formData: FormData = request.body;
                    let containsFile: boolean = false;

                    formData.forEach((value, key): void => {
                        if (value instanceof File) {
                            containsFile = true;
                        }
                    });

                    if (containsFile) {
                        this.snack.openErrorSnackBar('The uploaded file/content is too large. Please upload a smaller one.')
                        return throwError((): null => null);
                    }
                } else {
                    this.snack.openErrorSnackBar('A network error occurred. Please check your internet connection or try again later.');
                    return throwError((): null => null);
                }
            }

            let displayError: string = 'An error has occurred; please try again later.'

            const emailVerificationErrors: Record<string, string> = {
                invalid_verification_code: 'The verification code is incorrect.',
                verification_expired: 'Verification has expired. Please sign in again.',
                verification_locked: 'Too many incorrect codes. Please sign in again.',
                email_required: 'Please enter a valid email address.'
            };
            if (request.url.includes('/users/login/sso/') && emailVerificationErrors[error.error?.message]) {
                return throwError(() => emailVerificationErrors[error.error.message]);
            }

            if (error.error?.detail) {
                displayError = error.error.detail;
            } else if (error.error?.title) {
                if (Array.isArray(error.error?.title)) {
                    displayError = error.error?.title[0];
                } else {
                    displayError = error.error?.title;
                }
            } else if (error.error?.text) {
                if (Array.isArray(error.error?.text)) {
                    displayError = error.error?.text[0];
                } else {
                    displayError = error.error?.text;
                }
            } else if (error.error?.message?.en) {
                displayError = error.error.message.en;
            } else if (error.error?.message) {
                if (typeof error.error.message == "object") {
                    let message: string = 'Error: '
                    for (const property in error.error.message) {
                        message = message + `${property}: ${error.error.message[property]} `
                    }
                    displayError = message;
                } else {
                    displayError = JSON.stringify(error.error.message);
                }
            } else if (error.error?.error) {
                displayError = error.error.error;
            } else if (error.message) {
                displayError = error.message;
            }

            return throwError(displayError);
        }));
    }
}
